"use client";

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useParams } from "next/navigation";
import { Languages, Search } from "lucide-react";
import { Link, usePathname, useRouter } from "@/i18n/routing";

/**
 * The nav's icon controls. QA: "add hover effect to the search, language,
 * profile icon buttons". The tint is the same `#f5fbf8` every 關注 button uses,
 * with the glyph taking the brand green.
 */
export const NAV_ICON_BUTTON =
  "flex items-center justify-center rounded-[3px] text-[#1d1d1f] transition-colors hover:bg-[#f5fbf8] hover:text-[#108a60]";

const LOCALES: { value: "zh-TW" | "zh-CN"; label: string }[] = [
  { value: "zh-TW", label: "繁體中文" },
  { value: "zh-CN", label: "简体中文" },
];

export function SearchAction({ iconSize = 24 }: { iconSize?: number }) {
  return (
    // The colour lives on the anchor as a class so `hover:` can win; lucide
    // icons default to `currentColor`. Setting it inline on the icon, as this
    // did, outranks every hover utility.
    <Link
      href="/search"
      aria-label="搜尋"
      className={NAV_ICON_BUTTON}
    >
      <Search style={{ width: iconSize, height: iconSize }} strokeWidth={1.8} />
    </Link>
  );
}

export function LanguageAction({
  iconSize = 24,
  panelAlign = "left",
}: {
  iconSize?: number;
  panelAlign?: "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const params = useParams<{ locale?: string }>();
  const pathname = usePathname();
  const router = useRouter();
  const currentLocale = (params?.locale ?? "zh-TW") as string;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const panelStyle: CSSProperties = {
    position: "absolute",
    top: "calc(100% + 8px)",
    [panelAlign]: 0,
    zIndex: 70,
    minWidth: 160,
    backgroundColor: "#ffffff",
    border: "1px solid rgba(0,0,0,0.1)",
    borderRadius: 4,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    padding: 4,
  };

  const choose = (locale: "zh-TW" | "zh-CN") => {
    setOpen(false);
    if (locale === currentLocale) return;
    router.replace(pathname, { locale });
  };

  return (
    <div ref={containerRef} className="relative inline-flex">
      <button
        type="button"
        aria-label="語言"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={NAV_ICON_BUTTON}
      >
        <Languages
          style={{ width: iconSize, height: iconSize }}
          strokeWidth={1.8}
        />
      </button>
      {open ? (
        <ul style={panelStyle} role="menu" aria-label="切換語言">
          {LOCALES.map((l) => {
            const active = l.value === currentLocale;
            return (
              <li key={l.value}>
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  onClick={() => choose(l.value)}
                  className="flex w-full items-center justify-between hover:bg-[#f5f7f6]"
                  style={{
                    padding: "8px 12px",
                    fontSize: 14,
                    lineHeight: "20px",
                    color: active ? "#0e8f63" : "#1d1d1f",
                    fontWeight: active ? 700 : 400,
                    borderRadius: 2,
                  }}
                >
                  <span>{l.label}</span>
                  {active ? <span aria-hidden>✓</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
